import * as React from 'react'

import moment from 'moment'

import { getLabels } from '@/app/components/timeline/timelineHelper'

export type GridBlockProps = {
  height: number
  width: number
  startTime: any
  isMonth: boolean
  weekly?: boolean
  days?: number
  headerHeight?: number
  bottomPadding?: number
  timeToX?: (time: any) => number
  secondsInPeriod?: number
  onDayClick?: (date: moment.Moment) => void
}

const PL_WEEKDAYS = ['Nd', 'Pn', 'Wt', 'Śr', 'Czw', 'Pt', 'Sb']

const formatDayHeaderLabel = (m: moment.Moment): string => {
  const dd = String(m.date()).padStart(2, '0')
  const mm = String(m.month() + 1).padStart(2, '0')
  return `${dd}-${mm}, ${PL_WEEKDAYS[m.day()]}`
}

const WeeklyGrid: React.FC<GridBlockProps> = (props) => {
  const days = props.days ?? 7
  const lineColor = '#E5E6E6'
  const totalHeight = props.height
  const headerHeight = props.headerHeight ?? 24
  const bottomPadding = props.bottomPadding ?? 40
  const BOTTOM_GAP = 12
  const start = moment(props.startTime)

  const xFor = (m: moment.Moment): number => {
    if (props.timeToX) return props.timeToX(m)
    const total = props.secondsInPeriod ?? days * 24 * 3600
    const secs = m.diff(start, 'seconds')
    return total > 0 ? (secs / total) * props.width : 0
  }

  const dayBoundaries: { x: number; label: string; date: moment.Moment }[] = []
  for (let d = 0; d < days; d++) {
    const m = start.clone().add(d, 'days')
    dayBoundaries.push({ x: xFor(m), label: formatDayHeaderLabel(m), date: m })
  }
  const middayTicks: number[] = []
  for (let d = 0; d < days; d++) {
    middayTicks.push(xFor(start.clone().add(d, 'days').add(12, 'hours')))
  }
  const endX = xFor(start.clone().add(days, 'days'))

  const gridTop = totalHeight - bottomPadding 

  return (
    <div

      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        height: totalHeight,
        width: props.width,
        pointerEvents: 'none',
      }}
      className="tw-border-t tw-border-base-300  "
    >
      {dayBoundaries.map((b, i) =>
        i === 0 ? null : (
          <div
            key={`sep-${i}`}
            style={{
              position: 'absolute',
              left: b.x,
              top: 0,
              height: totalHeight - BOTTOM_GAP,
              borderLeft: `1px solid ${lineColor}`,
            }}
          />
        ),
      )}

      {dayBoundaries.map((b, i) => {
        const nextX =
          i + 1 < dayBoundaries.length ? dayBoundaries[i + 1].x : endX
        const dayWidth = nextX - b.x
        return (
          <div
            key={`hdr-${i}`}
            onClick={
              props.onDayClick ? () => props.onDayClick?.(b.date) : undefined
            }
            style={{
              position: 'absolute',
              left: b.x,
              top: 0,
              width: dayWidth,
              height: headerHeight,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'start',
              padding: '4px 8px',
              whiteSpace: 'nowrap',
              pointerEvents: props.onDayClick ? 'auto' : 'none',
              cursor: props.onDayClick ? 'pointer' : 'default',
            }}
          >
            <span className="tw-text-[9px] tw-font-medium tw-text-content-base/80">
              {b.label}
            </span>
          </div>
        )
      })}

     
      {middayTicks.map((x, i) => (
        <div
          key={`tick12-${i}`}
          style={{
            position: 'absolute',
            left: x,
            top: headerHeight - 15,
            height: totalHeight - headerHeight + 15 - BOTTOM_GAP,
            borderLeft: `1px solid ${lineColor}`,
            display: 'flex',
            alignItems: 'flex-end',
            paddingLeft: 2,
            letterSpacing: 1.1,
            fontSize: 10,
          }}
        >
          <span className="tw-text-content-base tw-opacity-60">12</span>
        </div>
      ))}

      {dayBoundaries.map((b, i) => (
        <div
          key={`tick00-${i}`}
          style={{
            position: 'absolute',
            left: b.x,
            top: gridTop,
            height: bottomPadding - BOTTOM_GAP,
            display: 'flex',
            alignItems: 'flex-end',
            paddingLeft: 2,
            letterSpacing: 1.1,
            fontSize: 10,
          }}
        >
          <span className="tw-text-content-base tw-opacity-60">00</span>
        </div>
      ))}
      <div
        key="tick00-end"
        style={{
          position: 'absolute',
          left: endX,
          top: gridTop,
          height: bottomPadding - BOTTOM_GAP,
          display: 'flex',
          alignItems: 'flex-end',
          letterSpacing: 1.1,
          fontSize: 10,
        }}
      >
        <span
          className="tw-text-content-base tw-opacity-60"
          style={{ transform: 'translateX(-100%)', paddingRight: 3 }}
        >
          00
        </span>
      </div>
    </div>
  )
}

export const GridBlock: React.FC<GridBlockProps> = React.memo(
  (props: GridBlockProps) => {
    if (props.weekly) {
      return <WeeklyGrid {...props} />
    }

    const labels = getLabels(props.width, props.startTime, props.isMonth)

    if (labels.length < 2) return null

    const lineColor = '#e3e7f65c'
    const containerWidth = props.width
    const firstLabelOffset = labels[0].fraction * containerWidth

    return (
      <div
        className="tw-flex tw-items-end"
        style={{
          position: 'absolute',
          bottom: 0,
          pointerEvents: 'none',
        }}
      >
        {labels.slice(0, -1).map((gl, i) => {
          const colWidth =
            (labels[i + 1].fraction - gl.fraction) * containerWidth
          return (
            <div
              key={i}
              className="tw-flex tw-items-end tw-justify-start"
              style={{
                width: colWidth,
                height: props.height,
                borderLeft: `1px solid ${lineColor}`,
                marginLeft: i === 0 ? firstLabelOffset : 0,
                paddingLeft: 2,
                letterSpacing: 1.1,
                fontSize: 10,
              }}
            >
              <span className="tw-text-content-base tw-opacity-60">
                {gl.label}
              </span>
            </div>
          )
        })}

        <div
          style={{
            position: 'absolute',
            left: containerWidth,
            bottom: 0,
            height: props.height,
            borderLeft: `1px solid ${lineColor}`,
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <span
            className="tw-text-content-base tw-opacity-60"
            style={{
              transform: 'translateX(-100%)',
              paddingRight: 3,
              letterSpacing: 1.1,
              fontSize: 10,
              whiteSpace: 'nowrap',
            }}
          >
            {labels[labels.length - 1].label}
          </span>
        </div>
      </div>
    )
  },
)

GridBlock.displayName = 'GridBlock'
